/**
 * The assistant's answer, which the model writes as GitHub-flavoured Markdown.
 * Raw HTML in the text is never parsed into markup (react-markdown's default,
 * kept by not adding rehype-raw), images are dropped so an answer cannot load
 * a third-party URL, and only https links to this product or Instagram become anchors. The text is
 * re-parsed on every streamed chunk; an unclosed construct renders as plain
 * text until its closing characters arrive.
 */

import type { ReactNode } from 'react';
import Markdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { APP_ORIGIN, LANDING_ORIGIN, OPS_ORIGIN } from '../../../lib/origins';

/** Hosts an answer may link to: this product's own origins and Instagram, where every post it
 *  names lives. Any other URL -- one a caption or comment could have planted -- stays text. */
const LINKABLE_HOSTS: ReadonlySet<string> = new Set(
  [APP_ORIGIN, LANDING_ORIGIN, OPS_ORIGIN].map((origin) => new URL(origin).hostname),
);

function linkable(href: string | undefined): href is string {
  if (href === undefined) return false;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  if (url.protocol !== 'https:') return false;
  const host = url.hostname.toLowerCase();
  return (
    LINKABLE_HOSTS.has(host) ||
    host === globalThis.location?.hostname ||
    host === 'instagram.com' ||
    host.endsWith('.instagram.com')
  );
}

const components: Components = {
  h1: ({ children }) => (
    <h3 className="mt-2 text-title-md text-foreground first:mt-0">{children}</h3>
  ),
  h2: ({ children }) => (
    <h4 className="mt-2 text-title-sm text-foreground first:mt-0">{children}</h4>
  ),
  h3: ({ children }) => (
    <h5 className="mt-2 text-title-xs text-foreground first:mt-0">{children}</h5>
  ),
  h4: ({ children }) => <h6 className="mt-2 text-label text-foreground first:mt-0">{children}</h6>,
  p: ({ children }) => <p>{children}</p>,
  ul: ({ children }) => <ul className="list-disc space-y-1 pl-5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal space-y-1 pl-5">{children}</ol>,
  li: ({ children }) => <li className="pl-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ href, children }) =>
    linkable(href) ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary-ink underline underline-offset-4 hover:no-underline focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      >
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  blockquote: ({ children }) => (
    <blockquote className="flex flex-col gap-2 border-l-2 border-border pl-4 text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="border-border" />,
  code: ({ className, children }) => (
    <code
      className={
        className?.startsWith('language-')
          ? 'font-mono'
          : 'rounded-sm bg-muted px-1.5 py-0.5 font-mono text-mono-sm'
      }
    >
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="overflow-x-auto rounded-md border border-border bg-muted p-3 font-mono text-mono-sm [&>code]:bg-transparent [&>code]:p-0">
      {children}
    </pre>
  ),
  table: ({ children }) => (
    <div className="w-full overflow-x-auto rounded-md border border-border">
      <table className="w-full border-collapse text-body-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
  tr: ({ children }) => <tr className="border-b border-border last:border-b-0">{children}</tr>,
  th: ({ children, style }) => (
    <th style={style} className="px-3 py-2 text-left text-label whitespace-nowrap">
      {children}
    </th>
  ),
  td: ({ children, style }) => (
    <td style={style} className="px-3 py-2 align-top">
      {children}
    </td>
  ),
};

export function AnswerText(props: { text: string }): ReactNode {
  return (
    <div className="flex w-full min-w-0 flex-col gap-2 text-body text-foreground">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={components}
        disallowedElements={['img']}
        unwrapDisallowed
      >
        {props.text}
      </Markdown>
    </div>
  );
}
